import { Button, Checkbox, Modal, Result, Select, Typography, Divider } from "antd";
import { observer } from "mobx-react-lite";
import React, { useRef, useState } from "react";
import {
  analyzeImport,
  importLegacyFestivalData,
  parseLegacyJsonFile,
} from "../../../features/importLegacyData";
import type {
  ImportAnalysis,
  ImportOptions,
  ImportResult,
  LegacyFestivalData,
  SegmentDecision,
} from "../../../features/importLegacyData";
import type { FestivalCalcStore } from "../store";

interface Props {
  store: FestivalCalcStore;
}

type ImportState =
  | { phase: "idle" }
  | { phase: "confirm"; filename: string; data: LegacyFestivalData }
  | { phase: "mapping"; data: LegacyFestivalData; analysis: ImportAnalysis }
  | { phase: "importing" }
  | { phase: "done"; result: ImportResult }
  | { phase: "error"; message: string };

function sectionLabel(section: "infra" | "food" | "drink"): string {
  if (section === "infra") return "Infrastruktur";
  if (section === "food") return "Essen";
  return "Getränke";
}

export const ImportButton = observer(({ store }: Props) => {
  const [state, setState] = useState<ImportState>({ phase: "idle" });
  const [segmentDecisions, setSegmentDecisions] = useState<SegmentDecision[]>([]);
  const [itemsToOverwrite, setItemsToOverwrite] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  function openFilePicker() {
    inputRef.current?.click();
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    try {
      const data = await parseLegacyJsonFile(file);
      setState({ phase: "confirm", filename: file.name, data });
    } catch (err: any) {
      setState({ phase: "error", message: err.message });
    }
  }

  function proceedToMapping(data: LegacyFestivalData) {
    const analysis = analyzeImport(data, store);
    const decisions: SegmentDecision[] = analysis.segmentsToCreate.map((s) => ({
      importedName: s.name,
      type: "new",
    }));
    setSegmentDecisions(decisions);
    setItemsToOverwrite(new Set());

    if (analysis.segmentsToCreate.length === 0 && analysis.duplicates.length === 0) {
      runImport(data, { segmentDecisions: decisions, itemsToOverwrite: new Set() });
    } else {
      setState({ phase: "mapping", data, analysis });
    }
  }

  async function runImport(data: LegacyFestivalData, options: ImportOptions) {
    setState({ phase: "importing" });
    try {
      const result = await importLegacyFestivalData(data, store, options);
      setState({ phase: "done", result });
    } catch (err: any) {
      setState({ phase: "error", message: err.message ?? "Unbekannter Fehler beim Import." });
    }
  }

  function reset() {
    setState({ phase: "idle" });
  }

  const confirmData = state.phase === "confirm" ? state : null;
  const mappingState = state.phase === "mapping" ? state : null;
  const doneResult = state.phase === "done" ? state.result : null;
  const errorMsg = state.phase === "error" ? state.message : null;

  const infraCount = confirmData?.data?.infra?.length ?? 0;
  const essenCount = confirmData?.data?.essen?.length ?? 0;
  const getraenkeCount = (confirmData?.data?.getraenke ?? []).filter((g: any) => !g.isIngredient).length;

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".json"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <Button onClick={openFilePicker}>Import (alt)</Button>

      {/* Confirm dialog */}
      <Modal
        open={state.phase === "confirm"}
        title="Import aus alter Kalkulation"
        onCancel={reset}
        onOk={() => confirmData && proceedToMapping(confirmData.data)}
        okText="Weiter"
        cancelText="Abbrechen"
      >
        <Typography.Paragraph>
          Datei: <strong>{confirmData?.filename}</strong>
        </Typography.Paragraph>
        <Typography.Paragraph>
          Folgende Einträge werden <strong>hinzugefügt</strong> (bestehende Daten bleiben erhalten):
        </Typography.Paragraph>
        <ul>
          {infraCount > 0 && <li>{infraCount} Infrastruktur-Einträge</li>}
          {essenCount > 0 && <li>{essenCount} Essen-Einträge</li>}
          {getraenkeCount > 0 && <li>{getraenkeCount} Getränke-Einträge</li>}
        </ul>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Preise werden unter dem Szenario-Tag „Standard" importiert.
        </Typography.Text>
      </Modal>

      {/* Mapping dialog */}
      <Modal
        open={state.phase === "mapping"}
        title="Import-Einstellungen"
        onCancel={reset}
        onOk={() =>
          mappingState &&
          runImport(mappingState.data, { segmentDecisions, itemsToOverwrite })
        }
        okText="Import starten"
        cancelText="Abbrechen"
        width={560}
      >
        {mappingState && mappingState.analysis.segmentsToCreate.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Tage zuordnen
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Wähle für jeden importierten Tag, ob ein neuer Tag angelegt oder ein bestehender verwendet werden soll.
            </Typography.Text>
            {mappingState.analysis.segmentsToCreate.map((seg, i) => {
              const decision = segmentDecisions[i];
              return (
                <div key={seg.name} style={{ marginTop: 12 }}>
                  <Typography.Text strong>{seg.name}</Typography.Text>
                  <Typography.Text type="secondary"> ({seg.attendeeCount} Personen)</Typography.Text>
                  <Select
                    value={decision?.type === "existing" ? decision.existingId : "__new__"}
                    onChange={(val) => {
                      const next = [...segmentDecisions];
                      if (val === "__new__") {
                        next[i] = { importedName: seg.name, type: "new" };
                      } else {
                        next[i] = {
                          importedName: seg.name,
                          type: "existing",
                          existingId: val,
                          updateCount: false,
                        };
                      }
                      setSegmentDecisions(next);
                    }}
                    style={{ width: "100%", marginTop: 6 }}
                  >
                    <Select.Option value="__new__">Neu erstellen</Select.Option>
                    {store.config.eventSegments.map((es) => (
                      <Select.Option key={es.id} value={es.id}>
                        {es.name} ({es.attendeeCount} Personen)
                      </Select.Option>
                    ))}
                  </Select>
                  {decision?.type === "existing" && (
                    <Checkbox
                      style={{ marginTop: 6 }}
                      checked={decision.updateCount ?? false}
                      onChange={(e) => {
                        const next = [...segmentDecisions];
                        next[i] = { ...next[i], updateCount: e.target.checked };
                        setSegmentDecisions(next);
                      }}
                    >
                      Personenanzahl aktualisieren (
                      {store.config.eventSegments.find((s) => s.id === decision.existingId)?.attendeeCount ?? "?"}{" "}
                      → {seg.attendeeCount})
                    </Checkbox>
                  )}
                </div>
              );
            })}
          </>
        )}

        {mappingState &&
          mappingState.analysis.segmentsToCreate.length > 0 &&
          mappingState.analysis.duplicates.length > 0 && <Divider />}

        {mappingState && mappingState.analysis.duplicates.length > 0 && (
          <>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Bestehende Einträge
            </Typography.Title>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Folgende Einträge existieren bereits mit gleichem Namen. Sollen sie überschrieben werden? Nicht ausgewählte werden übersprungen.
            </Typography.Text>
            <div style={{ marginTop: 8 }}>
              {mappingState.analysis.duplicates.map((dup) => (
                <div key={`${dup.section}-${dup.name}`} style={{ marginTop: 6 }}>
                  <Checkbox
                    checked={itemsToOverwrite.has(dup.name.toLowerCase())}
                    onChange={(e) => {
                      const next = new Set(itemsToOverwrite);
                      if (e.target.checked) next.add(dup.name.toLowerCase());
                      else next.delete(dup.name.toLowerCase());
                      setItemsToOverwrite(next);
                    }}
                  >
                    <Typography.Text type="secondary">{sectionLabel(dup.section)}: </Typography.Text>
                    <strong>{dup.name}</strong>
                  </Checkbox>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal>

      {/* Importing spinner */}
      <Modal
        open={state.phase === "importing"}
        footer={null}
        closable={false}
        title="Import läuft…"
      >
        <Typography.Text>Daten werden in Parse gespeichert. Bitte warten…</Typography.Text>
      </Modal>

      {/* Success result */}
      <Modal
        open={state.phase === "done"}
        footer={<Button type="primary" onClick={reset}>Schließen</Button>}
        title="Import erfolgreich"
        onCancel={reset}
      >
        <Result
          status="success"
          title="Import abgeschlossen"
          subTitle={
            <div>
              {doneResult && (
                <>
                  <p>
                    {doneResult.infraCount} Infrastruktur · {doneResult.foodCount} Essen · {doneResult.drinkCount} Getränke
                    {(doneResult.recipeCount ?? 0) > 0 && ` · ${doneResult.recipeCount} Rezept(e)`}
                  </p>
                  {(doneResult.segmentsCreated?.length ?? 0) > 0 && (
                    <p>Neue Tage: {doneResult.segmentsCreated.join(", ")}</p>
                  )}
                  {doneResult.tagCreated && <p>Neuer Szenario-Tag erstellt: „{doneResult.tagCreated}"</p>}
                  {(doneResult.itemsSkipped ?? 0) > 0 && (
                    <p>{doneResult.itemsSkipped} Einträge übersprungen (Name bereits vorhanden)</p>
                  )}
                  {(doneResult.itemsOverwritten ?? 0) > 0 && (
                    <p>{doneResult.itemsOverwritten} Einträge überschrieben</p>
                  )}
                  {(doneResult.warnings?.length ?? 0) > 0 && (
                    <ul style={{ textAlign: "left", color: "#faad14" }}>
                      {doneResult.warnings.map((w: string, i: number) => (
                        <li key={i}>{w}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          }
        />
      </Modal>

      {/* Error */}
      <Modal
        open={state.phase === "error"}
        footer={<Button onClick={reset}>Schließen</Button>}
        title="Import fehlgeschlagen"
        onCancel={reset}
      >
        <Result status="error" title="Fehler beim Import" subTitle={errorMsg} />
      </Modal>
    </>
  );
});
